// 📄 components/StepPortDefinition.tsx
import React, { useState } from 'react';
import { Network, FolderTree, FileCode, CheckSquare, Square } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepPortDefinitionProps {
  // Pasamos la prop onChange general por si en el futuro decides persistir estos campos en la BD
  onChange?: (field: keyof GeneralsFormData, value: any) => void; 
}

export function StepPortDefinition({ onChange }: StepPortDefinitionProps) {
  // 📝 Estados locales para simular la interactividad exacta del manual de SAP
  const [portName, setPortName] = useState('CIGPORT');
  const [description, setDescription] = useState('ARIBA CIG Integración');
  const [rfcDestination, setRfcDestination] = useState('CIG_QS4');
  const [contentType, setContentType] = useState<'xml' | 'idoc'>('xml');
  const [soapProtocol, setSoapProtocol] = useState(true);

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Guía de Ruta SPRO de SAP */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">WE21</span> 
          <span>SPRO → Global Settings → Create Port Definition (XML HTTP)</span>
        </p>
      </div>

      <h3 className="text-lg font-medium text-blue-400">3. Port Definition (Puertos IDoc)</h3>
      <p className="text-sm text-gray-400">
        Configura el puerto lógico para el procesamiento y salida de los IDocs hacia el middleware de Ariba CIG.
      </p>

      {/* 🖥️ CLON DE LA INTERFAZ SAP "Ports in IDoc processing" */}
      <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-xl overflow-hidden bg-zinc-900/40">
        
        {/* Columna Izquierda: Simulación del Árbol de Puertos de SAP */}
        <div className="md:col-span-1 bg-zinc-900/90 border-r border-white/10 p-3 space-y-2 text-xs font-mono text-gray-400">
          <span className="text-[10px] text-gray-500 font-sans font-bold uppercase tracking-wider block border-b border-white/5 pb-1">Puertos SAP</span>
          
          <div className="space-y-1.5 select-none">
            <div className="flex items-center gap-1.5 opacity-60"><FolderTree className="w-3.5 h-3.5" /> Puertos</div>
            <div className="flex items-center gap-1.5 pl-3 opacity-60"><span>•</span> Transactional RFC</div>
            <div className="flex items-center gap-1.5 pl-3 opacity-60"><span>•</span> File</div>
            <div className="flex items-center gap-1.5 pl-3 opacity-60"><span>•</span> ABAP-PI</div>
            
            {/* Nodo XML HTTP Seleccionado */}
            <div className="flex items-center gap-1.5 pl-3 text-blue-400 font-bold"><FolderTree className="w-3.5 h-3.5" /> XML HTTP</div>
            <div className="flex items-center justify-between pl-6 bg-blue-500/10 border-l-2 border-blue-500 py-0.5 text-white pr-1">
              <span>⚡ {portName || 'NUEVO'}</span>
              <span className="text-[9px] text-gray-500 truncate max-w-[80px]">{description}</span>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Formulario de Parámetros de SAP */}
        <div className="md:col-span-2 p-4 space-y-4 text-white">
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block border-b border-white/5 pb-1">Maintenance Area</span>

          {/* Fila: Port y Description */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] text-gray-400 mb-0.5">Port</label>
              <input 
                type="text" 
                value={portName} 
                onChange={(e) => setPortName(e.target.value.toUpperCase())} 
                placeholder="ej. CIGPORT"
                className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs font-mono text-blue-400 uppercase focus:outline-none focus:border-blue-500" 
              />
            </div>
            <div className="col-span-2">
              <label className="block text-[11px] text-gray-400 mb-0.5">Description</label>
              <input 
                type="text" 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs text-yellow-200/90 focus:outline-none focus:border-blue-500" 
              />
            </div>
          </div>

          {/* Fila: RFC Destination */}
          <div>
            <label className="block text-[11px] text-gray-400 mb-0.5">RFC destination</label>
            <input 
              type="text" 
              value={rfcDestination} 
              onChange={(e) => setRfcDestination(e.target.value)} 
              placeholder="ej. CIG_QS4"
              className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-blue-500" 
            />
          </div>

          {/* Fila: Contenedores Estilo Radio (Content Type y HTTP Version) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Content Type Card */}
            <div className="bg-white/5 p-2.5 rounded-lg border border-white/5 space-y-1.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1">
                <FileCode className="w-3 h-3" /> Content Type
              </span>
              <div className="space-y-1 text-xs">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="radio" name="contentType" checked={contentType === 'xml'} onChange={() => setContentType('xml')} className="text-blue-500 focus:ring-0 bg-zinc-800 border-white/10 w-3 h-3" />
                  <span className={contentType === 'xml' ? 'text-white font-medium' : 'text-gray-400'}>Text/XML</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="radio" name="contentType" checked={contentType === 'idoc'} onChange={() => setContentType('idoc')} className="text-blue-500 focus:ring-0 bg-zinc-800 border-white/10 w-3 h-3" />
                  <span className={contentType === 'idoc' ? 'text-white font-medium' : 'text-gray-400'}>application/x-sap.idoc</span>
                </label>
              </div>
            </div>

            {/* HTTP Version Ficticio (Igual al manual de SAP) */}
            <div className="bg-white/5 p-2.5 rounded-lg border border-white/5 space-y-1.5 opacity-50 cursor-not-allowed">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">HTTP Version</span>
              <div className="space-y-1 text-xs text-gray-500">
                <label className="flex items-center gap-2"><input type="radio" checked disabled className="w-3 h-3" /> Version 1.0</label>
                <label className="flex items-center gap-2"><input type="radio" disabled className="w-3 h-3" /> Version 1.1</label>
              </div>
            </div>
          </div>

          {/* Checkbox: SOAP Protocol */}
          <div className="pt-1">
            <label 
              onClick={() => setSoapProtocol(!soapProtocol)} 
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg border border-white/5 text-xs text-gray-300 cursor-pointer select-none transition-all"
            >
              {soapProtocol ? (
                <CheckSquare className="w-4 h-4 text-blue-400 fill-blue-500/10" />
              ) : (
                <Square className="w-4 h-4 text-gray-500" />
              )}
              <span className={soapProtocol ? 'text-white font-medium' : ''}>SOAP Protocol Box Activo</span>
            </label>
          </div>

        </div>
      </div>
    </div>
  );
}